import { auth } from 'api/Firebase/firebase-init'
import { VISITOR } from 'app/routes'
import { FullscreenIcon } from 'assets/icons'
import { ButtonLoading } from 'components/Buttons'
import {
  IOSInstallModal,
  IOS_INSTALL_DISMISSED_KEY,
} from 'components/IOSInstallModal/IOSInstallModal'
import { DesktopMenu, MobileMenu } from 'components/Menu'
import { KioskCountdownModal } from './KioskCountdownModal'
import { KioskNonAnonModal } from './KioskNonAnonModal'
import { KioskPrintWaitModal } from './KioskPrintWaitModal'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import FormattedMessage from 'utils/FormattedMessage'
import { shouldPromptIOSInstall } from 'utils/isMobile'
import { CssCustomisation } from './CssCustomisation'
import { useWizardForm } from 'store/useWizardForm'
import { useKioskPrintWait } from 'store/useKioskPrintWait'
import { useWizardSteps } from 'views/BookPage/useWizardSteps'
import { nextTick } from 'process'
import PoweredBy from 'components/PoweredBy'
import { useFirestoreReconnect } from 'utils/useFirestoreReconnect'
import { usePlace } from 'store/usePlace'
import { useKioskSession } from 'store/useKioskSession'
import { useKioskHeartbeat } from 'utils/useKioskHeartbeat'
import { useCurrentUser } from 'store/useCurrentUser'
import { useTickets } from 'store/useTickets'
import {
  KIOSK_AUTO_RESET_SCHEDULED_MS,
  KIOSK_AUTO_RESET_WALK_IN_MS,
  KIOSK_INACTIVITY_COUNTDOWN_SEC,
  KIOSK_INACTIVITY_TIMEOUT_MS,
  KIOSK_INACTIVITY_WARNING_MS,
} from 'constants/kiosk'

export const Layout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { placeId: urlPlaceId } = useParams()
  const [lastClick, setLastClick] = useState<number>()
  const parts = location.pathname.split('/')
  const pageName = parts[parts.length - 1]
  const isTicketPage = parts[parts.length - 2] === 'ticket'
  const isKioskPage = pageName === 'kiosk'
  const isKioskMode = location.state?.kioskMode === 'on' || pageName === 'kiosk'
  const timerIdRef = useRef<NodeJS.Timeout | null>(null)
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showNonAnonModal, setShowNonAnonModal] = useState<boolean>(false)
  const [secondLeft, setSecondLeft] = useState<number>(20)
  const { resetSteps } = useWizardSteps()
  const { resetWizardForm } = useWizardForm()
  const { printerInfo, clearPrinterInfo } = useKioskPrintWait()
  const doSignOutAndRedirectRef = useRef<(() => void) | null>(null)
  const { place } = usePlace()
  const [showIOSInstallModal, setShowIOSInstallModal] = useState(false)

  useFirestoreReconnect()

  useEffect(() => {
    if (
      shouldPromptIOSInstall() &&
      place?.messagingConfig?.useWebPushMessenger &&
      !localStorage.getItem(IOS_INSTALL_DISMISSED_KEY)
    ) {
      const timer = setTimeout(() => setShowIOSInstallModal(true), 3000)
      return () => clearTimeout(timer)
    }
  }, [place?.messagingConfig?.useWebPushMessenger])

  const clickOnUI = useCallback(() => {
    if (timerIdRef.current !== null) {
      clearTimeout(timerIdRef.current)
      timerIdRef.current = null
    }

    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }

    setShowModal(false)
    if (pageName === 'kiosk') return
    if (location.state?.kioskMode !== 'on') return
    setLastClick(Date.now())

    if (location.state?.kioskMode === 'on' && pageName !== 'kiosk') {
      timerIdRef.current = setTimeout(() => {
        let counter = KIOSK_INACTIVITY_COUNTDOWN_SEC
        intervalIdRef.current = setInterval(() => {
          counter--
          setShowModal(true)
          setSecondLeft(counter)
          if (counter === 0) {
            clearInterval(intervalIdRef.current!)
            intervalIdRef.current = null
            triggerKioskResetRef.current()
          }
        }, 1000)
      }, KIOSK_INACTIVITY_WARNING_MS)
    }
  }, [pageName, location.state?.kioskMode])

  const doSignOutAndRedirect = useCallback(() => {
    if (timerIdRef.current !== null) {
      clearTimeout(timerIdRef.current)
      timerIdRef.current = null
    }
    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }

    setShowModal(false)
    setShowNonAnonModal(false)

    auth.signOut()
    nextTick(() => {
      resetSteps()
      resetWizardForm(urlPlaceId)
      if (urlPlaceId) navigate(VISITOR.KIOSK.replace(':placeId', urlPlaceId))
    })
  }, [urlPlaceId, navigate, resetSteps, resetWizardForm])

  useEffect(() => {
    doSignOutAndRedirectRef.current = doSignOutAndRedirect
  }, [doSignOutAndRedirect])

  const triggerKioskResetRef = useRef<() => void>(() => {})
  useEffect(() => {
    triggerKioskResetRef.current = () => {
      if (auth.currentUser && !auth.currentUser.isAnonymous) {
        setShowModal(false)
        setShowNonAnonModal(true)
        return
      }
      doSignOutAndRedirectRef.current?.()
    }
  }, [])

  useEffect(() => {
    if (lastClick) {
      const timer = setTimeout(() => triggerKioskResetRef.current(), KIOSK_INACTIVITY_TIMEOUT_MS)
      return () => clearTimeout(timer)
    }
  }, [lastClick])

  // Auto-reset after booking: longer delay for scheduled appointments so the visitor
  // can note the date/time; shorter for walk-ins where only a queue number is shown.
  const autoResetDelay = location.state?.scheduledBooking
    ? KIOSK_AUTO_RESET_SCHEDULED_MS
    : KIOSK_AUTO_RESET_WALK_IN_MS
  useEffect(() => {
    if (!isKioskMode || !isTicketPage || printerInfo) return
    const timer = setTimeout(() => triggerKioskResetRef.current(), autoResetDelay)
    return () => clearTimeout(timer)
  }, [isKioskMode, isTicketPage, printerInfo, autoResetDelay])

  useEffect(() => {
    clickOnUI()
  }, [location.state?.kioskMode, pageName, clickOnUI])

  const { kioskCode } = useKioskSession()
  useKioskHeartbeat(isKioskMode ? place?.id : undefined, kioskCode)

  const { authUser } = useCurrentUser()
  const { subscribe: subscribeToTickets } = useTickets()
  useEffect(() => {
    if (!isKioskMode && authUser?.uid) {
      subscribeToTickets(authUser.uid)
      return () => useTickets.getState().unsubscribe()
    }
  }, [authUser?.uid, isKioskMode])

  return (
    <>
      <CssCustomisation />
      <div className='md:flex md:flex-col h-full' onClick={clickOnUI}>
        <Outlet />

        {!isKioskMode && !isKioskPage && (
          <>
            <div className='hidden md:block'>
              <DesktopMenu />
            </div>
            <div className='block md:hidden'>
              <MobileMenu />
            </div>
          </>
        )}
        {isKioskMode && !isKioskPage && (
          <div className='fixed top-4 left-1/2 transform -translate-x-1/2 md:left-auto md:right-4 md:transform-none'>
            <ButtonLoading
              onClick={() => triggerKioskResetRef.current()}
              style='w-full !mb-0 hover:bg-transparent bg-transparent text-white !flex !items-center !text-gray-800 dark:text-gray-100 dark:border-1'
            >
              <FullscreenIcon className='w-6 h-6 stroke-gray-800 dark:stroke-gray-100 mr-2' />
              <span className='text-gray-800 dark:text-gray-100'>
                <FormattedMessage id='kiosk.backModal.submit' />
              </span>
            </ButtonLoading>
          </div>
        )}
      </div>
      <div className='hidden md:block fixed md:left-[10px] md:bottom-[5px] z-1'>
        <PoweredBy />
      </div>
      <IOSInstallModal open={showIOSInstallModal} onDismiss={() => setShowIOSInstallModal(false)} />
      {isKioskMode && (
        <KioskNonAnonModal
          open={showNonAnonModal}
          onCancel={() => {
            setShowNonAnonModal(false)
            clickOnUI()
          }}
          onConfirm={() => doSignOutAndRedirectRef.current?.()}
        />
      )}
      {isKioskMode && (
        <KioskCountdownModal
          open={showModal}
          secondLeft={secondLeft}
          onCancel={clickOnUI}
          onConfirm={() => triggerKioskResetRef.current()}
        />
      )}
      {isKioskMode && printerInfo && (
        <KioskPrintWaitModal
          open={true}
          printingWaitTime={printerInfo.printingWaitTime}
          onClose={clearPrinterInfo}
        />
      )}
    </>
  )
}
